import { useEffect, useRef, useState } from 'react'

import { EMPTY_LIST, EMPTY_OBJECT } from '../../../../constants/common'
import { useTimeZone } from '../../../../hooks'

import {
  getCalendarGridRangeKey,
  getCalendarGridReadKey,
  getCalendarGridResponse,
} from './useCalendarGridItems.utils'

export function useCalendarGridItems({
  from,
  getItems,
  isEnabled,
  requestKey,
  to,
}) {
  const [items, setItems] = useState(EMPTY_LIST)
  const [dayLoads, setDayLoads] = useState(EMPTY_OBJECT)
  const [settingsHint, setSettingsHint] = useState(EMPTY_OBJECT)
  const [serverError, setServerError] = useState(null)

  const [itemsRangeKey, setItemsRangeKey] = useState(null)
  const [settledReadKey, setSettledReadKey] = useState(null)
  const [isReadPending, setReadPending] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)

  const readIdRef = useRef(0)
  const reloadsRef = useRef([])

  const timezone = useTimeZone()

  const rangeKey = getCalendarGridRangeKey({ from, to })

  const readKey = getCalendarGridReadKey({
    rangeKey,
    reloadKey,
    requestKey,
    timezone,
  })

  const isInitiated = settledReadKey !== null

  const isReading = isEnabled && (settledReadKey !== readKey || isReadPending)

  function settleReloads(nextItems) {
    reloadsRef.current.splice(0).forEach(resolveReload => {
      resolveReload(nextItems)
    })
  }

  async function readItems(readId) {
    setReadPending(true)

    return getItems({ from, timezone, to })
      .then(response => {
        if (readIdRef.current === readId) {
          const nextResponse = getCalendarGridResponse(response)

          setItems(nextResponse.items)
          setDayLoads(nextResponse.dayLoads)
          setSettingsHint(nextResponse.settingsHint)
          setItemsRangeKey(rangeKey)
          setServerError(null)

          settleReloads(nextResponse.items)
        }
      })
      .catch(error => {
        if (readIdRef.current === readId) {
          setServerError(error)

          settleReloads(EMPTY_LIST)
        }
      })
      .finally(() => {
        if (readIdRef.current === readId) {
          setSettledReadKey(readKey)
          setReadPending(false)
        }
      })
  }

  useEffect(() => {
    readIdRef.current += 1

    if (isEnabled) {
      readItems(readIdRef.current)
    }
  }, [from, isEnabled, reloadKey, requestKey, timezone, to])

  async function handleReload() {
    readIdRef.current += 1

    return new Promise(resolve => {
      reloadsRef.current.push(resolve)

      setReloadKey(prevKey => prevKey + 1)
    })
  }

  return {
    dayLoads,
    handleReload,
    isLoading: !isInitiated,
    isRangeLoading:
      isReading && (itemsRangeKey !== rangeKey || Boolean(serverError)),
    isRefetching: isReading && isInitiated,
    items,
    serverError,
    settingsHint,
  }
}
